import { useCallback, useEffect, useState } from "react";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useGetChannelMentorship } from "@/lib/hooks/conversations/use-get-channel-mentorship";
import { useMessageInputStore } from "@/lib/hooks/conversations/use-message-input";
import { useHubStore } from "@/components/hub/use-hub-store";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useUpdateMagicMomentState } from "@/lib/api/mentorships";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { Day1Banner } from "./day1-banner";
import { Day2Banner } from "./day2-banner";
import { Day2Modal } from "./day2-modal";
import { useMagicMomentStore } from "./use-magic-moment-store";

interface MentorMomentBannerProps {
  currentConversation: string;
}

export function MentorMomentBanner({
  currentConversation,
}: MentorMomentBannerProps) {
  const { mentorship } = useGetChannelMentorship();
  const day1FlagEnabled = useFeatureFlagEnabled("enable-day1-banner");
  const day2FlagEnabled = useFeatureFlagEnabled("enable-day2-magic-moment");

  const devDay = useDevOverridesStore((s) => s.magicMomentDay);
  const devForceDay2Modal = useDevOverridesStore((s) => s.forceDay2Modal);

  const day1Enabled = devDay ? true : day1FlagEnabled;
  const day2Enabled = devDay ? true : day2FlagEnabled;

  const { track } = useTrackEvent();
  const { mutate: updateMagicMomentState } = useUpdateMagicMomentState();
  const resetView = useMagicMomentStore((s) => s.resetView);
  const isModalDismissed = useMagicMomentStore((s) => s.isModalDismissed);
  const openHub = useHubStore((s) => s.open);
  const navigateToHubView = useHubStore((s) => s.navigateToView);
  const setMessageInput = useMessageInputStore((s) => s.setMessageInput);

  const [showModal, setShowModal] = useState(false);

  // Extract stable primitives from metadata
  const magicMoment = (mentorship?.metadata as Record<string, any>)
    ?.magic_moment;
  const dayState = devDay ?? (magicMoment?.day_state as string | undefined);

  const mentorshipId = mentorship?.id || null;
  const teenGender = (magicMoment?.teen_gender || null) as
    | "male"
    | "female"
    | null;
  const modalDismissed =
    devDay === "day2"
      ? !devForceDay2Modal
      : !!magicMoment?.day2_modal_dismissed;
  const bannerDismissed = devDay
    ? false
    : dayState === "day2"
      ? !!magicMoment?.day2_banner_dismissed
      : !!magicMoment?.day1_banner_dismissed;
  const teenId = mentorship?.mentee?.id || null;

  useEffect(() => {
    resetView();
  }, [currentConversation, resetView]);

  useEffect(() => {
    if (
      dayState === "day2" &&
      day2Enabled &&
      !modalDismissed &&
      currentConversation &&
      !isModalDismissed(mentorshipId ?? "")
    ) {
      setShowModal(true);
    }
  }, [
    dayState,
    day2Enabled,
    modalDismissed,
    currentConversation,
    isModalDismissed,
  ]);

  const dismissBanner = useCallback(
    (reason: "x_button" | "action_completed" | "message_sent") => {
      if (!mentorshipId || !dayState) return;
      const eventName =
        dayState === "day1"
          ? PostHogEvents.DAY1_BANNER_DISMISS
          : PostHogEvents.DAY2_BANNER_DISMISS;
      track(eventName, { teen_id: teenId, dismiss_reason: reason });
      updateMagicMomentState({
        mentorshipId,
        fields:
          dayState === "day1"
            ? { day1_banner_dismissed: true }
            : { day2_banner_dismissed: true },
      });
    },
    [mentorshipId, dayState, teenId, track, updateMagicMomentState],
  );

  const handleShareMoment = useCallback(() => {
    dismissBanner("action_completed");
    openHub();
    navigateToHubView("create-moment");
  }, [dismissBanner, openHub, navigateToHubView]);

  const handleStartSwap = useCallback(() => {
    dismissBanner("action_completed");
    openHub();
    navigateToHubView("create-swap");
  }, [dismissBanner, openHub, navigateToHubView]);

  const handleInsertToChat = useCallback(
    (text: string) => {
      setMessageInput(currentConversation, text);
      // Fire banner_dismiss with reason=message_sent to track intent.
      // Note: we don't actually wait for the send — the mentor may still edit.
      // The banner stays visible so they can re-reference the template.
      if (mentorshipId && dayState === "day2") {
        track(PostHogEvents.DAY2_BANNER_DISMISS, {
          teen_id: teenId,
          dismiss_reason: "message_sent",
        });
      }
    },
    [
      currentConversation,
      setMessageInput,
      mentorshipId,
      dayState,
      teenId,
      track,
    ],
  );

  if (!dayState || bannerDismissed) {
    return null;
  }

  if (dayState === "day1" && day1Enabled) {
    return (
      <div className="mx-4">
        <Day1Banner teenId={teenId} conversationId={currentConversation} />
      </div>
    );
  }

  if (dayState === "day2" && day2Enabled) {
    return (
      <div className="mx-4">
        {showModal && mentorshipId && (
          <Day2Modal
            mentorshipId={mentorshipId}
            teenId={teenId}
            conversationId={currentConversation}
            open={showModal}
            onDismiss={() => setShowModal(false)}
          />
        )}
        <Day2Banner
          teenGender={teenGender}
          teenId={teenId}
          conversationId={currentConversation}
          onShareMoment={handleShareMoment}
          onStartSwap={handleStartSwap}
          onInsertToChat={handleInsertToChat}
        />
      </div>
    );
  }

  return null;
}
